import React, { useMemo } from "react";
import { ExportPdfHeader } from "../../../ExportPdfHeader";
import { BudgetCategory, BudgetContingency, BudgetI } from "../types";
import { TopSheetGridPrint } from "./TopSheetGridPrint";
import { DetailedBudgetGridPrint } from "./DetailedBudgetGridPrint";
import { useSelector } from "react-redux";
import { AppState } from "store/reducers/Reducer";
import { resolveBudgetDisplayLocation } from "utils/resolveBudgetDisplayLocation";

interface BudgetPrintProp {
  data: BudgetCategory[];
  project: string;
  createdAt: string;
  updatedAt: string;
  budgetDetail: Omit<BudgetI, "categories"> | undefined;
  budgetContingency: BudgetContingency | null;
}

export const BudgetPrint: React.FC<BudgetPrintProp> = ({
  data,
  project,
  createdAt,
  updatedAt,
  budgetDetail,
  budgetContingency,
}) => {
  const projectProductionState = useSelector(
    (state: AppState) => state.project.features?.production_state
  );

  const displayLocation = useMemo(
    () =>
      resolveBudgetDisplayLocation({
        rerunParams: budgetDetail?.rerun_params,
        assumptions: budgetDetail?.assumptions,
        projectProductionState,
      }),
    [budgetDetail?.assumptions, budgetDetail?.rerun_params, projectProductionState]
  );

  return (
    <div>
      <div className="printheader">
        <ExportPdfHeader
          created_at={"Created: " + createdAt}
          title="Budget"
          project={"Title: " + project}
          updated_at=""
        />
      </div>
      <div className="afterpagebreak"></div>
      <div className="flex flex-col">
        <div className="sm:-mx-6 lg:-mx-8">
          <div className="min-w-full sm:px-6 lg:px-8">
            <div className="bg-[#625c5f] uppercase text-white px-4 py-4 text-lg">
              Budget Topsheet
            </div>
            <div className="flex justify-between border-b border-black pb-5 my-6 p-4 bg-[#F8F9FA] border">
              <div className="text-base">
                <div className="mt-2">
                  <b>Title: </b>
                  {project}
                </div>
                <div className="mt-2">
                  <b>Production Company: </b>
                  {budgetDetail?.document?.project?.workspace?.name || ""}
                </div>
                <div className="mt-2">
                  <b>Estimated Shoot Days: </b>
                  {budgetDetail?.estimated_shoot_days}
                </div>
                <div className="mt-2">
                  <b>Date Created: </b>
                  {createdAt}
                </div>
              </div>
              <div className="w-1/2">
                <div className="font-base font-bold">Assumptions</div>
                <ul className="text-base font-base text-left">
                  {displayLocation.productionBaseLabel ? (
                    <li>
                      <span className="uppercase">&bull; Production base: </span>
                      <span>{displayLocation.productionBaseLabel}</span>
                    </li>
                  ) : null}
                  {budgetDetail?.assumptions?.map((assume, assumeid) => {
                    if (assume.name?.toUpperCase() === "LOCATION") return null;
                    return (
                      <li key={assumeid}>
                        <span className="uppercase">
                          &bull; {assume.name && `${assume.name}${assume.value && ":"} `}
                        </span>
                        <span>{assume.value}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
            <TopSheetGridPrint
              data={data}
              project={project}
              createdAt={createdAt}
              updatedAt={updatedAt}
              budgetDetail={budgetDetail}
              budgetContingency={budgetContingency}
            />
            <div className="afterpagebreak"></div>
            <div className="bg-[#625c5f] uppercase text-white px-4 py-4 mb-6 text-lg">
              Detailed Budget
            </div>
            <DetailedBudgetGridPrint
              data={data}
              project={project}
              budgetDetail={budgetDetail}
              budgetContingency={budgetContingency}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
